/**
 * InterviewScheduleListPage — Issue #188.
 *
 * Read-only list of interview schedules. Triggering notifications is out of
 * scope for this Issue.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_INTERVIEW_STATUS_LABELS,
  useHrInterviewSchedules,
  type HrInterviewScheduleDto,
} from '@/hooks/useHrIssue188';

const STATUS_TONE: Record<HrInterviewScheduleDto['status'], string> = {
  draft: 'bg-slate-100 text-slate-700',
  invited: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  rescheduled: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-700',
  canceled: 'bg-slate-200 text-slate-600',
  no_show: 'bg-red-100 text-red-700',
};

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function InterviewScheduleListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.INTERVIEW_READ);

  const { items, total, loading, error, fetchSchedules } = useHrInterviewSchedules();
  const [status, setStatus] = useState<HrInterviewScheduleDto['status'] | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchSchedules({ status: status || undefined, page, limit });
  }, [canRead, status, page, fetchSchedules]);

  if (!canRead) return <AccessDenied resource="面试排期" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">面试排期</h1>
        <p className="text-sm text-slate-500">
          排期保存面试官系统 ID 列表；联系方式、姓名不写入排期正文。
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={status}
            onChange={(e) => {
              setStatus((e.target.value as HrInterviewScheduleDto['status']) || '');
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(Object.entries(HR_INTERVIEW_STATUS_LABELS) as [
              HrInterviewScheduleDto['status'],
              string,
            ][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2 w-32">候选人 ID</th>
              <th className="px-3 py-2 w-24">方式</th>
              <th className="px-3 py-2 w-24">状态</th>
              <th className="px-3 py-2 w-40">开始</th>
              <th className="px-3 py-2 w-40">结束</th>
              <th className="px-3 py-2">地点 / 链接</th>
              <th className="px-3 py-2 w-32">通知发送</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{row.candidateId}</td>
                <td className="px-3 py-2">{row.mode}</td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                      STATUS_TONE[row.status],
                    )}
                  >
                    {HR_INTERVIEW_STATUS_LABELS[row.status]}
                  </span>
                </td>
                <td className="px-3 py-2">{fmt(row.scheduledStart)}</td>
                <td className="px-3 py-2">{fmt(row.scheduledEnd)}</td>
                <td className="px-3 py-2 text-slate-600">
                  {row.location ?? row.meetingUrl ?? '—'}
                </td>
                <td className="px-3 py-2">{fmt(row.notificationSentAt)}</td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  没有排期
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
